import { useState, useEffect, useCallback } from 'react';

/**
 * useRestaurantData — fetches the menu.json for a given restaurant slug.
 * The JSON lives at /restaurants/{slug}/menu.json
 *
 * Returns: { data, loading, error, retry }
 */
export default function useRestaurantData(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const basePath =
    process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.'
      ? process.env.PUBLIC_URL.replace(/\/$/, '')
      : '';

  const normalizedSlug = decodeURIComponent(String(slug || ''))
    .trim()
    .toLowerCase();

  const fetchData = useCallback(async () => {
    if (!normalizedSlug) return;
    setLoading(true);
    setError(null);
    try {
      const encodedSlug = encodeURIComponent(normalizedSlug);
      const res = await fetch(`${basePath}/restaurants/${encodedSlug}/menu.json`);
      if (!res.ok) throw new Error(`Restaurant "${normalizedSlug}" not found`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [basePath, normalizedSlug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, retry: fetchData };
}
